import React, { useState, useRef, useEffect } from 'react'

import { useGlobalOverlay } from '../../context/GlobalOverlayContext'
import {
  IconContainer,
  StyledIcon,
  CountBadge,
  OrangeBadge,
  Tooltip,
  MarketsList,
  MarketItem,
  MarketItemIcon,
  MarketName,
  ExternalItem,
  MarketItemOrangeBadge,
} from './styles/MarketIcon.styles'

// Types
interface Market {
  name: string
  icon: string
}

interface MarketIconProps {
  markets: Market[] | Market | null
  isClient?: boolean
}

const MarketIcon: React.FC<MarketIconProps> = ({
  markets,
  isClient = false,
}) => {
  const [showTooltip, setShowTooltip] = useState<boolean>(false)
  const iconRef = useRef<HTMLDivElement>(null)
  const { addOverlay, removeOverlay } = useGlobalOverlay()

  // Get the first market for display and check if valid markets exist
  const hasMarkets =
    markets && (Array.isArray(markets) ? markets.length > 0 : true)
  const primaryMarket = hasMarkets
    ? Array.isArray(markets)
      ? markets[0]
      : markets
    : null
  const multipleMarkets =
    hasMarkets && Array.isArray(markets) && markets.length > 1
  const tooltipKey = `market-tooltip-${primaryMarket?.name || 'unknown'}`

  // Clean up the overlay when component unmounts or when tooltip is hidden
  useEffect(() => {
    return () => {
      removeOverlay(tooltipKey)
    }
  }, [removeOverlay, tooltipKey])

  // Handle tooltip visibility
  useEffect(() => {
    if (hasMarkets && showTooltip) {
      const tooltipContent = (
        <Tooltip>
          <MarketsList>
            {Array.isArray(markets)
              ? markets.map((market: Market, index: number) => (
                  <MarketItem key={index}>
                    <MarketItemIcon src={market.icon} />
                    <MarketName>{market.name}</MarketName>
                  </MarketItem>
                ))
              : primaryMarket && (
                  <MarketItem>
                    <MarketItemIcon src={primaryMarket.icon} />
                    <MarketName>{primaryMarket.name}</MarketName>
                  </MarketItem>
                )}

            {!isClient && (
              <ExternalItem>
                <MarketItemOrangeBadge />
                <MarketName>= External dataset</MarketName>
              </ExternalItem>
            )}
          </MarketsList>
        </Tooltip>
      )

      addOverlay(tooltipKey, tooltipContent, iconRef.current, 'bottom-right')
    } else {
      removeOverlay(tooltipKey)
    }
  }, [
    showTooltip,
    markets,
    addOverlay,
    removeOverlay,
    tooltipKey,
    hasMarkets,
    primaryMarket,
    isClient,
  ])

  // Early return after all hooks
  if (!hasMarkets || !primaryMarket) {
    return <div style={{ display: 'flex', justifyContent: 'center' }}></div>
  }

  return (
    <IconContainer
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      isInteractive={hasMarkets}
    >
      <StyledIcon ref={iconRef} src={primaryMarket.icon} />

      {!isClient && <OrangeBadge />}

      {multipleMarkets && Array.isArray(markets) && (
        <CountBadge>
          {markets.length > 9 ? '9+' : `${markets.length}+`}
        </CountBadge>
      )}

      {/* Tooltip is now rendered through GlobalOverlayContext */}
    </IconContainer>
  )
}

export default MarketIcon
